import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Review, PaginatedResponse, Client } from '../../types';
import api from '../../services/api';
import RatingGraph from './RatingGraph';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { fullName } from '../../utils/name';
import { formatRating } from '../../utils/rating';
import './ReviewList.css';

interface ReviewListProps {
  reviews: PaginatedResponse<Review> | null;
  currentPage: number;
  onPageChange: (page: number) => void;
  onFiltersChange?: (filters: ReviewFilters) => void;
  onReviewDeleted?: () => void; // Optional callback to refetch list after delete
  loading?: boolean;
  clients?: Client[];
  clientId?: number | null; // For client users to filter graph by their client
}

interface ReviewFilters {
  search?: string;
  start_date?: string;
  end_date?: string;
  client_id?: number;
}

const ReviewList: React.FC<ReviewListProps> = ({ 
  reviews, 
  currentPage, 
  onPageChange,
  onFiltersChange,
  onReviewDeleted,
  loading = false,
  clients: clientsProp = [],
  clientId: clientIdProp = null
}) => {
  //console.log('ReviewList initial render');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isAdminUser = isAdmin();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [quickDateRange, setQuickDateRange] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [graphReviews, setGraphReviews] = useState<Review[]>([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  /** client_id -> threshold on 0–10 scale (from company review_threshold %) */
  const [thresholdsByClient, setThresholdsByClient] = useState<Record<number, number>>({});

  // Use clientIdProp directly for client users, or selectedClientId state for admin
  const [selectedClientId, setSelectedClientId] = useState<number | null>(clientIdProp || null);
  
  // Update selectedClientId when clientIdProp changes (for client users only)
  useEffect(() => {
    if (!isAdminUser && clientIdProp !== null && clientIdProp !== undefined) {
      // Use functional update to avoid dependency on selectedClientId
      setSelectedClientId(prev => prev !== clientIdProp ? clientIdProp : prev);
    }
  }, [clientIdProp, isAdminUser]); // Removed selectedClientId from deps to prevent loop
  
  // Mark initial mount as complete after first render
  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  // Load each company's bad-review threshold (review_threshold is 0–100; convert to 0–10)
  useEffect(() => {
    const clientIds = new Set<number>();
    if (clientIdProp != null) clientIds.add(clientIdProp);
    if (selectedClientId != null) clientIds.add(selectedClientId);
    clientsProp.forEach((c) => clientIds.add(c.id));
    reviews?.data?.forEach((r) => {
      if (r.client_id) clientIds.add(r.client_id);
    });

    if (clientIds.size === 0) {
      setThresholdsByClient({});
      return;
    }

    let cancelled = false;
    const load = async () => {
      const map: Record<number, number> = {};
      await Promise.all(
        Array.from(clientIds).map(async (id) => {
          try {
            const res = await api.get(`/settings?client_id=${id}`);
            const setting = (res.data || []).find((s: { key: string; value?: string }) => s.key === 'review_threshold');
            const percent = setting?.value != null && setting.value !== ''
              ? parseFloat(String(setting.value))
              : 70;
            map[id] = (Number.isNaN(percent) ? 70 : percent) / 10;
          } catch {
            map[id] = 7;
          }
        })
      );
      if (!cancelled) {
        setThresholdsByClient(map);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [reviews?.data, clientsProp, clientIdProp, selectedClientId]);

  const isBadReview = (review: Review): boolean => {
    const threshold = thresholdsByClient[review.client_id] ?? 7;
    return Number(review.rating) < threshold;
  };

  // Calculate date range based on quick date selection
  const calculateDateRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };
  
  // Helper function to apply filters - only if onFiltersChange is provided
  const applyFilters = (search: string, start: string, end: string, clientId: number | null = null) => {
    if (onFiltersChange) {
      onFiltersChange({
        search: search.trim() || undefined,
        start_date: start || undefined,
        end_date: end || undefined,
        client_id: clientId || undefined
      });
    }
  };

  // Handle quick date range change
  const handleQuickDateRangeChange = (value: string) => {
    setQuickDateRange(value);
    
    if (value === '') {
      // Clear date filters
      setStartDate('');
      setEndDate('');
      applyFilters(searchTerm, '', '', selectedClientId);
    } else {
      const days = parseInt(value);
      const { start, end } = calculateDateRange(days);
      setStartDate(start);
      setEndDate(end);
      applyFilters(searchTerm, start, end, selectedClientId);
    }
  };

  // Handle manual date range change
  const handleDateRangeChange = (type: 'start' | 'end', value: string) => {
    let newStartDate = startDate;
    let newEndDate = endDate;
    
    if (type === 'start') {
      newStartDate = value;
      setStartDate(value);
      setQuickDateRange(''); // Clear quick range when manually setting dates
    } else {
      newEndDate = value;
      setEndDate(value);
      setQuickDateRange(''); // Clear quick range when manually setting dates
    }
    
    // Apply filters immediately with updated dates
    applyFilters(searchTerm, newStartDate, newEndDate, selectedClientId);
  };

  // Handle search term change with debounce (only for search, not dates)
  // Only trigger when searchTerm changes, NOT when selectedClientId changes from prop
  useEffect(() => {
    // Skip initial mount to avoid unnecessary API call
    if (isInitialMount) {
      return;
    }
    
    // Only apply filters if onFiltersChange is provided (for admin dashboard)
    if (!onFiltersChange) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      applyFilters(searchTerm, startDate, endDate, selectedClientId);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isInitialMount]); // Only trigger on searchTerm change

  // Determine which clientId to use for graph (clientIdProp for client users, selectedClientId for admin)
  const graphClientId = useMemo(() => {
    return !isAdminUser ? clientIdProp : selectedClientId;
  }, [isAdminUser, clientIdProp, selectedClientId]);

  // Fetch all reviews for the graph when filters change (with debounce for search)
  useEffect(() => {
    // On initial mount, fetch immediately
    if (isInitialMount) {
      fetchGraphReviews();
      return;
    }
    
    // For subsequent changes, debounce search but not date changes
    const timeoutId = setTimeout(() => {
      fetchGraphReviews();
    }, searchTerm.trim() ? 500 : 0); // Debounce only for search, immediate for date changes

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate, graphClientId]); // Use computed graphClientId

  const fetchGraphReviews = async () => {
    try {
      setGraphLoading(true);
      const params = new URLSearchParams({
        per_page: '1000',
        page: '1'
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      // Include client_id filter if set (for both admin and client users)
      // Use graphClientId which is computed based on user type
      if (graphClientId !== null && graphClientId !== undefined) {
        params.append('client_id', graphClientId.toString());
      }

      const response = await api.get<PaginatedResponse<Review>>(
        `/reviews?${params.toString()}`
      );
      
      let allGraphReviews = response.data.data;
      
      // Fetch additional pages if needed (limit to 3 pages for performance)
      if (response.data.last_page > 1 && response.data.total > 1000) {
        const maxPages = Math.min(response.data.last_page, 3);
        const additionalPages = [];
        
        for (let page = 2; page <= maxPages; page++) {
          try {
            const pageParams = new URLSearchParams(params);
            pageParams.set('page', page.toString());
            const pageResponse = await api.get<PaginatedResponse<Review>>(
              `/reviews?${pageParams.toString()}`
            );
            additionalPages.push(...pageResponse.data.data);
          } catch (err) {
            console.error(`Failed to fetch page ${page}:`, err);
            break;
          }
        }
        
        allGraphReviews = [...allGraphReviews, ...additionalPages];
      }
      
      setGraphReviews(allGraphReviews);
    } catch (error) {
      console.error('Failed to fetch graph reviews:', error);
      setGraphReviews([]);
    } finally {
      setGraphLoading(false);
    }
  };

  // Fetch clients for admin users if not provided via prop
  const [fetchedClients, setFetchedClients] = useState<Client[]>([]);
  
  useEffect(() => {
    if (isAdminUser && clientsProp.length === 0) {
      api.get<Client[]>('/clients')
        .then(response => setFetchedClients(response.data))
        .catch(error => console.error('Failed to fetch clients:', error));
    } else {
      setFetchedClients([]);
    }
  }, [isAdminUser, clientsProp.length]);
  
  // Use fetched clients if no prop clients, otherwise use prop clients
  const clientsToUse: Client[] = clientsProp.length > 0 ? clientsProp : fetchedClients;

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedReviews = () => {
    if (!reviews?.data || !sortColumn) return reviews?.data || [];
    
    return [...reviews.data].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = fullName(a).toLowerCase();
          bValue = fullName(b).toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'phone':
          aValue = a.phone || '';
          bValue = b.phone || '';
          break;
        case 'rating':
          aValue = a.rating;
          bValue = b.rating;
          break;
        case 'client':
          aValue = a.client?.client_name || '';
          bValue = b.client?.client_name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleClientFilterChange = (clientId: string) => {
    const id = clientId === '' ? null : parseInt(clientId, 10);
    setSelectedClientId(id);
    // Apply filters with new client ID
    applyFilters(searchTerm, startDate, endDate, id);
  };

  const handleReviewClick = (reviewId: number) => {
    navigate(`/dashboard/reviews/${reviewId}`);
  };

  const handleEditClick = (e: React.MouseEvent, reviewId: number) => {
    e.stopPropagation();
    navigate(`/dashboard/reviews/${reviewId}/edit`);
  };

  const handleDeleteClick = async (e: React.MouseEvent, reviewId: number) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }
    try {
      setDeletingId(reviewId);
      await api.delete(`/reviews/${reviewId}`);
      onReviewDeleted?.();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete review');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-badge status-approved';
      case 'pending':
        return 'status-badge status-pending';
      default:
        return 'status-badge status-pending';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setQuickDateRange('');
    setStartDate('');
    setEndDate('');
    setSelectedClientId(null);
    applyFilters('', '', '', null);
  };

  const hasActiveFilters = searchTerm.trim() || startDate || endDate || selectedClientId !== null;

  // Prepare filters for graph
  const graphFilters = {
    search: searchTerm.trim() || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined
  };

  return (
    <div className="review-list">
      {/* Advanced Filters Section */}
      <div className="filters-container">
        <div className="filters-header">
          <h3 className="filters-title">Filter Reviews</h3>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear All Filters
            </button>
          )}
        </div>
        
        <div className="filters-grid">
          {/* Search Field */}
          <div className="filter-group">
            <label htmlFor="search-input" className="filter-label">
              Search (Name, Email, Phone)
            </label>
            <input
              id="search-input"
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input search-input-field"
            />
          </div>

          {/* Quick Date Range Dropdown */}
          <div className="filter-group">
            <label htmlFor="quick-date-range" className="filter-label">
              Quick Date Range
            </label>
            <select
              id="quick-date-range"
              value={quickDateRange}
              onChange={(e) => handleQuickDateRangeChange(e.target.value)}
              className="filter-select"
            >
              <option value="">Select range...</option>
              <option value="7">Last 7 days</option>
              <option value="15">Last 15 days</option>
              <option value="30">Last 30 days</option>
            </select>
          </div>

          {/* Start Date */}
          <div className="filter-group">
            <label htmlFor="start-date" className="filter-label">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="filter-input date-input"
              max={endDate || new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* End Date */}
          <div className="filter-group">
            <label htmlFor="end-date" className="filter-label">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="filter-input date-input"
              min={startDate}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Company Filter (Admin only) */}
          {isAdminUser && (
            <div className="filter-group">
              <label htmlFor="company-filter" className="filter-label">
                Company
              </label>
              <select
                id="company-filter"
                value={selectedClientId || ''}
                onChange={(e) => handleClientFilterChange(e.target.value)}
                className="filter-select"
              >
                <option value="">All Companies</option>
                {clientsToUse.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Rating Graph */}
      <RatingGraph reviews={graphReviews} filters={graphFilters} loading={graphLoading} />

      {/* Reviews Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading">Loading reviews...</div>
        </div>
      ) : reviews && reviews.data.length > 0 ? (
        <>
          <table className="reviews-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                  ID {sortColumn === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  First Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Last Name</th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                  Email {sortColumn === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                  Phone {sortColumn === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('rating')} style={{ cursor: 'pointer' }}>
                  Rating {sortColumn === 'rating' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                {isAdminUser && (
                  <th onClick={() => handleSort('client')} style={{ cursor: 'pointer' }}>
                    Company {sortColumn === 'client' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                )}
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortColumn === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {getSortedReviews().map((review) => {
                const bad = isBadReview(review);
                return (
                <tr 
                  key={review.id}
                  onClick={() => handleReviewClick(review.id)}
                  className={bad ? 'review-row-bad' : undefined}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{review.id}</td>
                  <td className="review-name">{review.first_name}</td>
                  <td className="review-name">{review.last_name}</td>
                  <td>{review.email}</td>
                  <td>{formatPhoneNumber(review.phone)}</td>
                  <td>
                    <div className="rating-cell">
                      <span className="rating-number">{formatRating(review.rating)}/10</span>
                      {bad && <span className="bad-review-badge">Bad Review</span>}
                    </div>
                  </td>
                  {isAdminUser && (
                    <td>
                      {review.client ? (
                        <span className="client-name">{review.client.client_name}</span>
                      ) : (
                        <span className="no-client">—</span>
                      )}
                    </td>
                  )}
                  <td>
                    <span className={getStatusBadgeClass(review.status || 'pending')}>
                      {review.status || 'pending'}
                    </span>
                  </td>
                  <td>{new Date(review.created_at).toLocaleDateString()}</td>
                  <td className="review-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm btn-edit"
                      onClick={(e) => handleEditClick(e, review.id)}
                      title="Edit review"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-delete"
                      onClick={(e) => handleDeleteClick(e, review.id)}
                      disabled={deletingId === review.id}
                      title="Delete review"
                    >
                      {deletingId === review.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {reviews.total > 20 && reviews.last_page > 1 && (
            <div className="pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {reviews.current_page} of {reviews.last_page} (Total: {reviews.total})
              </span>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === reviews.last_page}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-results-container">
          <div className="no-results">
            {hasActiveFilters 
              ? 'No reviews found matching your filters.' 
              : 'No reviews found.'}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewList;
